/**
 * Language Slice
 *
 * Zustand slice for language and translation state management.
 * Handles language detection, persistence, and translation retrieval.
 *
 * @validates Requirements 4.1, 4.2, 4.3, 4.4
 */

import type { StateCreator } from 'zustand';
import type { LocaleCode } from '../services/languageService';
import {
  getInitialLanguage,
  storeLanguage,
  DEFAULT_LANGUAGE,
} from '../services/languageService';

// Import translation files
import frTranslations from '../locales/fr.json';
import enTranslations from '../locales/en.json';
import esTranslations from '../locales/es.json';
import deTranslations from '../locales/de.json';
import ptTranslations from '../locales/pt.json';
import itTranslations from '../locales/it.json';

// ============================================================================
// Types
// ============================================================================

/**
 * Translations object structure - nested JSON with dot notation access
 */
export type Translations = Record<string, unknown>;

/**
 * Language state
 */
export interface LanguageState {
  /** Current active language */
  currentLanguage: LocaleCode;
  /** Loaded translations for current language */
  translations: Translations;
  /** Whether language is being loaded/initialized */
  isLanguageLoading: boolean;
}

/**
 * Language actions
 */
export interface LanguageActions {
  /**
   * Set the current language and load translations
   * Persists to localStorage
   */
  setLanguage: (locale: LocaleCode) => Promise<void>;

  /**
   * Get a translated string by key using dot notation
   * Returns the key itself if translation not found (fallback)
   * @example t('home.hero.title') => "Find all words"
   */
  t: (key: string) => string;

  /**
   * Get the current locale code for formatting purposes
   */
  getLocale: () => LocaleCode;

  /**
   * Initialize language on app startup
   * Called once during application bootstrap
   */
  initializeLanguage: () => Promise<void>;
}

export type LanguageSlice = LanguageState & LanguageActions;

// ============================================================================
// Translation Loading
// ============================================================================

/**
 * Map of locale codes to their translation objects
 */
const translationMap: Record<LocaleCode, Translations> = {
  fr: frTranslations,
  en: enTranslations,
  es: esTranslations,
  de: deTranslations,
  pt: ptTranslations,
  it: itTranslations,
};

/**
 * Load translations for a given locale
 */
function loadTranslations(locale: LocaleCode): Translations {
  return translationMap[locale] || translationMap[DEFAULT_LANGUAGE];
}

// ============================================================================
// Nested Key Access
// ============================================================================

/**
 * Get a value from a nested object using dot notation
 * @param obj - The object to traverse
 * @param path - Dot-separated path (e.g., 'home.hero.title')
 * @returns The value at the path, or undefined if not found
 */
function getNestedValue(obj: Translations, path: string): unknown {
  const keys = path.split('.');
  let current: unknown = obj;

  for (const key of keys) {
    if (current === null || current === undefined) {
      return undefined;
    }
    if (typeof current !== 'object') {
      return undefined;
    }
    current = (current as Record<string, unknown>)[key];
  }

  return current;
}

// ============================================================================
// Initial State
// ============================================================================

/**
 * Initial language state
 */
const initialLanguageState: LanguageState = {
  currentLanguage: DEFAULT_LANGUAGE,
  translations: translationMap[DEFAULT_LANGUAGE],
  isLanguageLoading: true,
};

// ============================================================================
// Slice Creation
// ============================================================================

/**
 * Create the language slice for the Zustand store
 * 
 * Note: Uses LanguageSlice as the store type parameter since the slice
 * is self-contained and will be combined with other slices in the main store.
 */
export const createLanguageSlice: StateCreator<LanguageSlice, [], [], LanguageSlice> = (
  set,
  get
) => ({
  // Initial state
  ...initialLanguageState,

  // Actions
  setLanguage: async (locale: LocaleCode) => {
    // Load translations for the new locale
    const translations = loadTranslations(locale);

    // Update state
    set({
      currentLanguage: locale,
      translations,
      isLanguageLoading: false,
    });

    // Persist to localStorage
    storeLanguage(locale);
  },

  t: (key: string): string => {
    const { translations } = get();
    const value = getNestedValue(translations, key);

    // Return the translation if it's a string, otherwise return the key as fallback
    if (typeof value === 'string') {
      return value;
    }

    // Fallback: return the key itself
    return key;
  },

  getLocale: (): LocaleCode => {
    return get().currentLanguage;
  },

  initializeLanguage: async () => {
    set({ isLanguageLoading: true });

    try {
      // Get the initial language (from localStorage or browser detection)
      const initialLocale = getInitialLanguage();

      // Load translations
      const translations = loadTranslations(initialLocale);

      // Update state
      set({
        currentLanguage: initialLocale,
        translations,
        isLanguageLoading: false,
      });
    } catch (error) {
      console.error('Failed to initialize language:', error);

      // Fallback to default language
      const translations = loadTranslations(DEFAULT_LANGUAGE);
      set({
        currentLanguage: DEFAULT_LANGUAGE,
        translations,
        isLanguageLoading: false,
      });
    }
  },
});
